import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import {
  ProfessionalAuditOutcome,
  ProfessionalAuditType
} from '@mieterengel/database-types/types/enums'
import { Alert, Button, Cluster, ModernSearch } from 'component-library-next'

import Loader from '@app/common/Components/Loader'
import { IModernSearchItem } from '@app/common/helpers/IModernSearchItem'
import { Features } from '@app/Feature'
import { isFeatureEnabled } from '@app/Feature/Store/selectors'
import { IntlMessageKeys, useIntl } from '@app/intl/Intl'
import { FormBackup } from '@app/lib/cache/FormBackup/FormBackup'
import { useFormBackup } from '@app/lib/cache/FormBackup/useFormBackup'
import { VisuallyHidden } from '@app/lib/display/VisuallyHidden/VisuallyHidden'
import { ChoiceField } from '@app/lib/fields/ChoiceField/ChoiceField'
import { InputField } from '@app/lib/fields/InputField/InputField'
import { Radio } from '@app/lib/inputs/Radio/Radio'
import { StructuredText } from '@app/lib/inputs/RichEditor/types'
import {
  IHandleEmailTemplateChange,
  ISelectedEmailTemplate
} from '@app/modules/Messages/Components/AnswerArea/AnswerArea'
import { AuditReportEditor } from '@app/modules/Messages/Components/AuditReportEditor/AuditReportEditor'
import { EmailTemplatePreview } from '@app/modules/Messages/Components/EmailTemplatePreview/EmailTemplatePreview'
import { containsPlaceholders } from '@app/modules/Messages/Components/helpers/containsPlaceholders'
import { isFieldEmpty } from '@app/modules/Messages/Components/helpers/isFieldEmpty'
import { TemplatePicker } from '@app/modules/Messages/Components/LawyerAuditReport/TemplatePicker'
import { RequestedLetterNotAddedPopUp } from '@app/modules/Messages/Components/RequestedLetterNotAddedPopUp'
import LawyerChatAction from '@app/modules/Messages/store/actions'
import { Customer, Message } from '@app/queries/CustomerAsLawyer/models'

import AddDocuments from '../LawyerAnswer/AddDocuments'
import AuditDocuments from '../LawyerAnswer/AddDocuments/auditDocuments'

import './styles.scss'

const Documents = AuditDocuments(AddDocuments)

type Props = {
  answerError?: string
  lastQuestion: Message
  latestPurchase: string
  customer: Customer
  requestedLetterNotAdded: boolean
  emailTemplates?: IModernSearchItem[]
  selectedEmailTemplate?: ISelectedEmailTemplate
  setSelectedEmailTemplate?: (template: ISelectedEmailTemplate | null) => void
  handleEmailTemplateChange?: (props: IHandleEmailTemplateChange) => void
}

type LawyerAuditValues = {
  outcome?: ProfessionalAuditOutcome
  findings: StructuredText
  recommendations: StructuredText
}

export const LawyerAuditReport = ({
  answerError,
  lastQuestion,
  latestPurchase,
  customer,
  requestedLetterNotAdded,
  emailTemplates,
  selectedEmailTemplate,
  setSelectedEmailTemplate,
  handleEmailTemplateChange
}: Props) => {
  const [showRequestedLetterNotAddedPopUp, setShowRequestedLetterNotAddedPopUp] = useState(false)
  const [auditReportData, setAuditReportData] = useState<null | LawyerAuditValues>(null)
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const answerErrorFromState = useSelector((state: any) => state.lawyerChat.answerError)
  const sending = useSelector((state: any) => state.lawyerChat.sending)
  const displayLawyerTextTemplates = useSelector(isFeatureEnabled(Features.LAWYER_TEXT_TEMPLATES))

  const uid = `audit-report.${customer.id}`
  const { initialValues, clearBackup } = useFormBackup<LawyerAuditValues>({
    uid
  })
  const methods = useForm<LawyerAuditValues>({
    defaultValues: initialValues
  })

  const checkLetterAndSubmit = async (data: LawyerAuditValues) => {
    if (requestedLetterNotAdded) {
      setShowRequestedLetterNotAddedPopUp(true)
      setAuditReportData(data)
      return
    }
    await submitData(data)
  }

  const questionTopic = lastQuestion.topic.name

  const isUtilityBillIrrelevant = methods.watch('outcome') === ProfessionalAuditOutcome.Inconclusive

  const errors = methods.formState.errors

  const scrollToError = (fieldName) => {
    const errorElement = document.getElementsByClassName(fieldName)[0]
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    const errorField = Object.keys(errors)[0]
    if (errorField) {
      scrollToError(errorField)
    }
  }, [errors])

  const auditType = useMemo(() => {
    const advocardAIChecks = 'advocard-ai-checks'
    const utilityBillName = 'Nebenkosten'
    const renovationClauseName = 'Renovieren'
    if (latestPurchase === advocardAIChecks) {
      if (questionTopic === utilityBillName) {
        return ProfessionalAuditType.AdvocardUtilityBillAudit
      }
      if (questionTopic === renovationClauseName) {
        return ProfessionalAuditType.AdvocardRenovationClauseAudit
      }
    }
    if (isUtilityBillIrrelevant) {
      return ProfessionalAuditType.RoutineStandardAudit
    }
    if (questionTopic === utilityBillName) {
      return ProfessionalAuditType.RoutineUtilityBillAudit
    }
    return ProfessionalAuditType.RoutineStandardAudit
  }, [latestPurchase, questionTopic, isUtilityBillIrrelevant])

  const auditTypesThatNeedOutcomeField = [
    ProfessionalAuditType.AdvocardUtilityBillAudit,
    ProfessionalAuditType.AdvocardRenovationClauseAudit,
    ProfessionalAuditType.RoutineUtilityBillAudit
  ]
  const showOutcomeField = auditTypesThatNeedOutcomeField.includes(auditType)

  const submitData = async (data: LawyerAuditValues) => {
    dispatch(
      LawyerChatAction.submitAuditReport({
        data: {
          outcome: isUtilityBillIrrelevant ? undefined : data.outcome,
          findings: data.findings,
          recommendations: data.recommendations,
          auditType
        },
        latestMessage: lastQuestion,
        templateId: selectedEmailTemplate?.id,
        customerId: customer.id
      })
    )
    !!setSelectedEmailTemplate && setSelectedEmailTemplate(null)
    methods.reset()
    clearBackup()
  }

  return (
    <div className='lawyer-audit__wrapper'>
      {sending ? <Loader /> : null}
      <FormProvider {...methods}>
        <FormBackup uid={uid} />
        {!!displayLawyerTextTemplates ? <TemplatePicker /> : null}
        <form onSubmit={methods.handleSubmit(checkLetterAndSubmit)}>
          {showOutcomeField && (
            <section className='lawyer-audit-section'>
              <h3>Ergebnis im Überblick</h3>
              <ChoiceField<ProfessionalAuditOutcome>
                name='outcome'
                label={(labelProps) => (
                  <VisuallyHidden>
                    <label {...labelProps}>effective</label>
                  </VisuallyHidden>
                )}
                rules={{
                  required: {
                    value: true,
                    message: formatMessage({ id: 'AuditReport.Error.Required' })
                  }
                }}
                render={({ register }) => (
                  <Cluster role='radiogroup' className={'outcome'}>
                    <Radio {...register({ value: ProfessionalAuditOutcome.Pass })}>
                      {formatMessage({
                        id: `AuditReport.${questionTopic}.Effective` as IntlMessageKeys
                      })}
                    </Radio>
                    <Radio {...register({ value: ProfessionalAuditOutcome.Fail })}>
                      {formatMessage({
                        id: `AuditReport.${questionTopic}.Ineffective` as IntlMessageKeys
                      })}
                    </Radio>
                    {latestPurchase !== 'advocard-ai-checks' && (
                      <Radio {...register({ value: ProfessionalAuditOutcome.Inconclusive })}>
                        {formatMessage({
                          id: `AuditReport.${questionTopic}.Irrelevant` as IntlMessageKeys
                        })}
                      </Radio>
                    )}
                  </Cluster>
                )}
              />
            </section>
          )}
          <section className='lawyer-audit-section' data-testid={'lawyer__audit__findings'}>
            <h3>Ergebnis und Begründung im Detail</h3>

            <InputField
              name={'findings'}
              hideLabel={true}
              label='Ergebnis und Begründung im Detail'
              rules={{
                validate: (value) => {
                  if (!value || isFieldEmpty(value)) {
                    return formatMessage({ id: 'AuditReport.Error.Required' })
                  }
                  const hasPlaceholder = containsPlaceholders(value)
                  if (hasPlaceholder) {
                    return formatMessage({ id: 'AuditReport.Error.Placeholder' })
                  }
                  return true
                }
              }}
              render={({ inputProps }) => <AuditReportEditor {...inputProps} />}
            />
          </section>

          <section className='lawyer-audit-section' data-testid={'lawyer__audit__recommendations'}>
            <h3>Handlungsempfehlung</h3>
            <InputField
              name={'recommendations'}
              hideLabel={true}
              label='Handlungsempfehlung'
              rules={{
                validate: (value) => {
                  if (!value) {
                    return true
                  }
                  const hasPlaceholder = containsPlaceholders(value)
                  if (hasPlaceholder) {
                    return formatMessage({ id: 'AuditReport.Error.Placeholder' })
                  }
                  return true
                }
              }}
              render={({ inputProps }) => <AuditReportEditor {...inputProps} />}
            />
          </section>

          {latestPurchase !== 'advocard-ai-checks' && (
            <>
              <section className={'lawyer-audit__choose-template'}>
                <h3>Vorlage für Vermieter anschreiben hinzufügen</h3>
                <ModernSearch
                  placeholder='Vorlage auswählen'
                  noOptionsMessage='Vorlage auswählen'
                  groupedOptions={emailTemplates || []}
                  onChange={handleEmailTemplateChange}
                />
              </section>

              {selectedEmailTemplate && (
                <EmailTemplatePreview
                  emailTemplate={selectedEmailTemplate}
                  onClose={() => {
                    !!setSelectedEmailTemplate && setSelectedEmailTemplate(null)
                  }}
                />
              )}
            </>
          )}

          <section className='lawyer-audit-section'>
            <div className='lawyer-audit__document'>
              <div className='lawyer-audit__docuemnt-list'>
                <Documents />
              </div>
            </div>
          </section>
          <div className='lawyer-audit__submit'>
            <Button
              disabled={sending}
              size={Button.sizes.medium}
              type={Button.types.submit}
              testId='lawyer__audit__submit'
            >
              Prüfungsbericht senden
            </Button>
          </div>
          {(answerError || answerErrorFromState) && (
            <Alert color={Alert.colors.warning}>
              Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.
            </Alert>
          )}
        </form>
      </FormProvider>
      {showRequestedLetterNotAddedPopUp && (
        <RequestedLetterNotAddedPopUp
          closePopUp={() => setShowRequestedLetterNotAddedPopUp(false)}
          sendAnswer={async () => {
            setShowRequestedLetterNotAddedPopUp(false)
            auditReportData && (await submitData(auditReportData))
          }}
        />
      )}
    </div>
  )
}
